import { Chart, registerables } from 'chart.js';
import * as SunCalc from 'suncalc';
import tzlookup from 'tz-lookup';
import { AppState } from '../state';
import { getMoonPhaseDetails } from '../utils/astronomy';
import {
  ForecastBinaryService,
  DecodedPointForecast,
  getRawHourOffsetsForModel
} from './ForecastBinaryService';
import { getElevationAt, interpolateSoundingTemp, interpolateSoundingWind } from './PoiDataService';
import { getGoldenWindowRating, getDarkSkiesRating } from '../utils/goldenWindowRating';
import { LegendTrendChartService } from './LegendTrendChartService';
import themeService from './ThemeService';
import {
  TierAccessService,
  getRequiredTierForLayer,
  hasTierAccess,
} from './TierAccessService';

Chart.register(...registerables);

export interface ModelMetaInfo {
  id: string;
  label: string;
  color: string;
  maxHours: number;
  isAvailableInHorizon: boolean;
}

export const MODEL_COLORS: Record<string, string> = {
  ecmwf: '#3b82f6', // Blue
  gfs: '#ef4444',   // Red
  hrrr: '#10b981',  // Emerald
  rrfs: '#8b5cf6',  // Purple
  rdps: '#f59e0b',  // Amber
  nbm: '#06b6d4',   // Cyan
  blend: '#eab308', // Gold
  icon13: '#ec4899',// Pink
  gdps: '#94a3b8',  // Slate
  raqdps: '#14b8a6',// Teal
  gefs: '#f97316',  // Orange
  gefs_dust: '#d97706', // Amber
  seasonal: '#a855f7',
  skysk_hres_topo: '#0284c7', // Sky Blue
  nbm_highres: '#0284c7'
};

export const MODEL_NOMINAL_MAX_HOURS: Record<string, number> = {
  rrfs: 84,
  hrrr: 48,
  rdps: 84,
  gfs: 240,
  icon13: 180,
  blend: 240,
  nbm: 240,
  skysk_hres_topo: 48,
  nbm_highres: 48,
  ecmwf: 240,
  gdps: 240,
  raqdps: 72,
  gefs: 120,
  gefs_dust: 120,
  seasonal: 240
};

export class ModelComparisonChartService {
  private static activeCharts: Chart[] = [];
  private static pendingRequestId: number = 0;
  private static cachedForecasts = new Map<string, DecodedPointForecast | null>();
  private static currentTimestamps: number[] = [];
  private static currentTimeStepMs: number | null = null;

  public static clearCache(): void {
    this.cachedForecasts.clear();
  }

  public static destroyAll(): void {
    for (const chart of this.activeCharts) {
      try {
        chart.destroy();
      } catch (_e) {
        // ignore
      }
    }
    this.activeCharts = [];
  }

  public static resizeAll(): void {
    for (const chart of this.activeCharts) {
      try {
        chart.resize();
      } catch (_e) {
        // ignore
      }
    }
  }

  public static cancelPending(): void {
    this.pendingRequestId++;
    this.destroyAll();
  }

  public static getModelColor(modelId: string): string {
    const clean = modelId.toLowerCase().replace(/^gw_v2_/, '');
    return MODEL_COLORS[clean] || MODEL_COLORS[modelId.toLowerCase()] || '#cbd5e1';
  }

  public static getModelMaxHours(modelId: string, forecast?: DecodedPointForecast | null): number {
    if (forecast && forecast.numHours > 0) return forecast.numHours;
    return MODEL_NOMINAL_MAX_HOURS[modelId.toLowerCase()] || 240;
  }

  private static getLocalDayStartUtcMs(date: Date, timeZone: string): number {
    const dFmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const parts = dFmt.formatToParts(date);
    const Y = parseInt(parts.find(p => p.type === 'year')!.value, 10);
    const M = parseInt(parts.find(p => p.type === 'month')!.value, 10);
    const D = parseInt(parts.find(p => p.type === 'day')!.value, 10);

    const approxUtc = Date.UTC(Y, M - 1, D, 0, 0, 0);
    const p2 = dFmt.formatToParts(new Date(approxUtc));
    const y2 = parseInt(p2.find(p => p.type === 'year')!.value, 10);
    const m2 = parseInt(p2.find(p => p.type === 'month')!.value, 10);
    const d2 = parseInt(p2.find(p => p.type === 'day')!.value, 10);
    const h2 = parseInt(p2.find(p => p.type === 'hour')!.value, 10) % 24;
    const min2 = parseInt(p2.find(p => p.type === 'minute')!.value, 10);

    const targetDayUtc = Date.UTC(Y, M - 1, D, 0, 0, 0);
    const parsedDayUtc = Date.UTC(y2, m2 - 1, d2, 0, 0, 0);

    let diffHours = h2 + min2 / 60;
    if (parsedDayUtc > targetDayUtc) diffHours += 24;
    if (parsedDayUtc < targetDayUtc) diffHours -= 24;

    return approxUtc - diffHours * 3600 * 1000;
  }

  public static getThemeColors(): {
    tickColor: string;
    gridColor: string;
    subtleGridColor: string;
    titleColor: string;
    cursorColor: string;
    consensusColor: string;
  } {
    const isLight = themeService.isLightPalette();
    const currentTheme = themeService.getTheme();
    const consensusColor = currentTheme === 'scifi' ? '#38bdf8' : (currentTheme === 'earth' ? '#86a37c' : (currentTheme === 'forest' ? '#22c55e' : (currentTheme === 'paper' ? '#1d4ed8' : '#00d4ff')));
    return {
      tickColor: isLight ? '#190f05' : 'rgba(255, 255, 255, 0.7)',
      gridColor: isLight ? 'rgba(90, 67, 45, 0.20)' : 'rgba(255, 255, 255, 0.08)',
      subtleGridColor: isLight ? 'rgba(90, 67, 45, 0.10)' : 'rgba(255, 255, 255, 0.04)',
      titleColor: isLight ? '#190f05' : 'rgba(255, 255, 255, 0.65)',
      cursorColor: consensusColor,
      consensusColor: consensusColor
    };
  }

  private static applyConsensusToDatasets(
    datasets: any[],
    timestampsCount: number,
    metricDecimals: number = 1
  ): void {
    if (datasets.length < 2) return;
    const themeColors = this.getThemeColors();
    const consensusData: (number | null)[] = new Array(timestampsCount);

    for (let t = 0; t < timestampsCount; t++) {
      let sum = 0;
      let count = 0;
      for (const ds of datasets) {
        if (ds.data && ds.data[t] !== null && typeof ds.data[t] === 'number' && !isNaN(ds.data[t])) {
          sum += ds.data[t];
          count++;
        }
      }
      if (count > 0) {
        const factor = Math.pow(10, metricDecimals);
        consensusData[t] = Math.round((sum / count) * factor) / factor;
      } else {
        consensusData[t] = null;
      }
    }

    // Fade existing individual model datasets
    for (const ds of datasets) {
      const origColor = ds.borderColor;
      ds.borderWidth = 1.2;
      ds.pointRadius = 0;
      ds.pointHoverRadius = 2;
      if (typeof origColor === 'string' && origColor.startsWith('#') && origColor.length === 7) {
        ds.borderColor = origColor + '40';
      } else if (typeof origColor === 'string' && origColor.startsWith('rgba')) {
        ds.borderColor = origColor.replace(/[\d\.]+\)$/, '0.30)');
      } else if (typeof origColor === 'string' && origColor.startsWith('rgb(')) {
        ds.borderColor = origColor.replace('rgb(', 'rgba(').replace(')', ', 0.30)');
      }
      if (ds.backgroundColor && ds.backgroundColor !== 'transparent') {
        ds.backgroundColor = 'transparent';
      }
    }

    // Prepend prominent consensus ensemble dataset
    datasets.unshift({
      label: '✨ Consensus Blend (Model Average)',
      data: consensusData,
      borderColor: themeColors.consensusColor,
      backgroundColor: 'transparent',
      borderWidth: 3.5,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 8,
      tension: 0.35,
      spanGaps: true,
      order: -1
    });
  }

  private static createCursorPlugin() {
    return {
      id: 'mcCursorLine',
      afterDraw: (chart: any) => {
        if (!ModelComparisonChartService.currentTimeStepMs || ModelComparisonChartService.currentTimestamps.length < 2 || !chart.scales.x) return;
        const startMs = ModelComparisonChartService.currentTimestamps[0];
        const endMs = ModelComparisonChartService.currentTimestamps[ModelComparisonChartService.currentTimestamps.length - 1];
        if (ModelComparisonChartService.currentTimeStepMs < startMs - 1800000 || ModelComparisonChartService.currentTimeStepMs > endMs + 1800000) return;
        const fracIdx = (ModelComparisonChartService.currentTimeStepMs - startMs) / 3600000;
        const x = chart.scales.x.getPixelForValue(fracIdx);
        const topY = chart.chartArea.top;
        const bottomY = chart.chartArea.bottom;
        const clampedX = Math.max(chart.chartArea.left, Math.min(x, chart.chartArea.right));
        const ctx = chart.ctx;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(clampedX, topY);
        ctx.lineTo(clampedX, bottomY);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = this.getThemeColors().cursorColor;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.restore();
      }
    };
  }

  private static buildHourlyGrid(startMs: number, horizonDays: number): { timestamps: number[] } {
    // 8 past hours + horizonDays * 24 future hours (matching master date/time slider)
    const totalHours = 8 + horizonDays * 24;
    const timestamps: number[] = new Array(totalHours + 1);
    for (let i = 0; i <= totalHours; i++) {
      timestamps[i] = startMs + i * 3600000;
    }
    return { timestamps };
  }

  private static formatLabels(timestamps: number[], tz: string, isImperial: boolean): string[] {
    return timestamps.map(ts => {
      const d = new Date(ts);
      const weekdayStr = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
      const hourStr = d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: isImperial, timeZone: tz }).replace(' ', '');
      return `${weekdayStr} ${hourStr}`;
    });
  }

  private static resampleSeries(
    rawOffsets: number[],
    rawValues: number[],
    cycleEpoch: number,
    timestamps: number[]
  ): (number | null)[] {
    const count = timestamps.length;
    const resampled = new Array<number | null>(count);
    const n = rawOffsets.length;

    if (n === 0) return resampled;
    if (n === 1) {
      resampled.fill(Number(rawValues[0]));
      return resampled;
    }

    const cycleEpochMs = cycleEpoch * 1000;
    let k = 0;

    for (let i = 0; i < count; i++) {
      const targetOffset = (timestamps[i] - cycleEpochMs) / 3600000;

      if (targetOffset <= rawOffsets[0]) {
        resampled[i] = Number(rawValues[0]);
        continue;
      }
      if (targetOffset >= rawOffsets[n - 1]) {
        // Return null past model horizon with 0.5h tolerance so curve stops naturally
        if (targetOffset > rawOffsets[n - 1] + 0.5) {
          resampled[i] = null;
        } else {
          resampled[i] = Number(rawValues[n - 1]);
        }
        continue;
      }

      while (k < n - 1 && rawOffsets[k + 1] < targetOffset) {
        k++;
      }

      const o0 = rawOffsets[k];
      const o1 = rawOffsets[k + 1];
      const v0 = Number(rawValues[k]);
      const v1 = Number(rawValues[k + 1]);

      if (o1 === o0 || targetOffset === o0) {
        resampled[i] = v0;
      } else if (targetOffset === o1) {
        resampled[i] = v1;
      } else {
        const t = (targetOffset - o0) / (o1 - o0);
        resampled[i] = Math.round((v0 * (1 - t) + v1 * t) * 10) / 10;
      }
    }

    return resampled;
  }

  private static extractProductSeries(
    forecast: DecodedPointForecast,
    productKey: string,
    isImperial: boolean
  ): { label: string; unit: string; values: number[] } {
    switch (productKey) {
      case 'hrtemp':
        return {
          label: 'Temperature',
          unit: isImperial ? '°F' : '°C',
          values: isImperial ? forecast.tempsF : forecast.tempsC
        };
      case 'clouds':
        return {
          label: 'Cloud Cover',
          unit: '%',
          values: forecast.cloudCoverTotal
        };
      case 'winds':
        return {
          label: 'Wind Speed',
          unit: isImperial ? 'mph' : 'km/h',
          values: isImperial ? forecast.windSpeedMph : forecast.windSpeedMph.map(v => Math.round(v * 1.60934))
        };
      case 'precip':
        return {
          label: 'Precip Rate',
          unit: isImperial ? 'in/h' : 'mm/h',
          values: isImperial ? forecast.precipRateMmH.map(v => Math.round((v / 25.4) * 100) / 100) : forecast.precipRateMmH
        };
      case 'precip_prob':
        return {
          label: 'Precip Probability',
          unit: '%',
          values: forecast.precipProb
        };
      case 'relative_humidity':
        return {
          label: 'Relative Humidity',
          unit: '%',
          values: forecast.relativeHumidity
        };
      case 'dewpoint':
        return {
          label: 'Dew Point',
          unit: isImperial ? '°F' : '°C',
          values: isImperial ? (forecast.dewpointF || []) : (forecast.dewpointC || [])
        };
      case 'freezing_level':
        return {
          label: 'Freezing Level',
          unit: isImperial ? 'ft' : 'm',
          values: isImperial ? forecast.freezingLevelFt : forecast.freezingLevelFt.map(v => Math.round(v * 0.3048))
        };
      case 'optics_dew_risk':
        return {
          label: 'Optics Dew Risk',
          unit: 'score',
          values: forecast.dewRiskScore
        };
      case 'darkskies':
        return {
          label: 'Dark Skies Quality',
          unit: 'score',
          values: forecast.darkskiesScore
        };
      case 'sun_intensity':
        return {
          label: 'Sun Intensity',
          unit: 'W/m²',
          values: forecast.sunIntensityWm2
        };
      case 'lightning':
      case 'thunderstorm':
        return {
          label: 'Thunderstorm Probability',
          unit: '%',
          values: forecast.lightningHazard
        };
      case 'windchill':
        return {
          label: 'Wind Chill',
          unit: isImperial ? '°F' : '°C',
          values: isImperial ? forecast.windchillF : forecast.windchillF.map(v => Math.round((v - 32) * (5 / 9)))
        };
      case 'smoke':
        return {
          label: 'Surface Smoke',
          unit: 'µg/m³',
          values: forecast.smokeSfc
        };
      case 'dust':
      case 'dust_forecast':
        return {
          label: 'Surface Dust',
          unit: 'µg/m³',
          values: forecast.smokeSfc
        };
      case 'aqi':
        return {
          label: 'Air Quality Index',
          unit: 'AQI',
          values: forecast.aqiValues
        };
      case 'snow_accum':
      case 'snow_depth':
        return {
          label: productKey === 'snow_depth' ? 'Snowpack Depth' : 'Snowfall Accumulation',
          unit: isImperial ? 'in' : 'cm',
          values: isImperial
            ? forecast.snowRateCmH.map(cm => Math.round((cm / 2.54) * 10) / 10)
            : forecast.snowRateCmH
        };
      case 'turbulence':
      case 'aviation_turbulence':
        return {
          label: 'Aviation Turbulence',
          unit: 'EDR',
          values: forecast.turbulenceEdr || []
        };
      default:
        return {
          label: 'Value',
          unit: '',
          values: isImperial ? forecast.tempsF : forecast.tempsC
        };
    }
  }

  public static getScoreQualitativeTier(score: number, productKey: string): string {
    if (productKey === 'darkskies') {
      return getDarkSkiesRating(score);
    }
    if (productKey === 'optics_dew_risk') {
      if (score >= 80) return 'Severe Dew / Fog Risk';
      if (score >= 60) return 'High Condensation';
      if (score >= 40) return 'Moderate Dew Risk';
      if (score >= 20) return 'Low Risk';
      return 'Safe / Dry Optics';
    }
    if (productKey === 'turbulence' || productKey === 'aviation_turbulence') {
      if (score >= 0.70) return 'Extreme Turbulence';
      if (score >= 0.45) return 'Severe Turbulence';
      if (score >= 0.20) return 'Moderate Turbulence';
      if (score >= 0.10) return 'Light Turbulence';
      return 'Smooth Air';
    }
    if (productKey === 'lightning' || productKey === 'thunderstorm') {
      if (score >= 80) return 'Extreme Hazard';
      if (score >= 55) return 'Severe Hazard';
      if (score >= 35) return 'Moderate Risk';
      if (score >= 15) return 'Slight Risk';
      return 'Minimal / Clear';
    }
    // sunrise, sunset, goldenhour, alpenglow:
    return getGoldenWindowRating(score);
  }

  public static async renderComparison(
    container: HTMLElement,
    ephemerisContainer: HTMLElement,
    state: AppState,
    modelsList: { id: string; label: string }[]
  ): Promise<ModelMetaInfo[]> {
    this.destroyAll();
    const reqId = ++this.pendingRequestId;

    const poi = state.selectedPoi;
    if (!poi) return [];

    const lat = poi.lat;
    const lon = poi.lng;
    const tz = tzlookup(lat, lon);
    const isImperial = state.units === 'imperial';
    const horizonDays = state.modelComparisonHorizon || 3;
    const mode = state.modelComparisonMode || 'single_product';
    const productKey = state.modelComparisonProduct || 'hrtemp';
    const scaleMode = state.modelComparisonScale || 'auto';
    const disabledModels = new Set(state.modelComparisonDisabledModels || []);

    // Daily discrete products (sunrise, goldenhour, alpenglow) use grouped bar charts
    if (['sunrise', 'goldenhour', 'alpenglow'].includes(productKey)) {
      container.innerHTML = '';
      ephemerisContainer.innerHTML = '';
      const modelMeta = await this.renderDailyEventCharts(
        container,
        state,
        modelsList,
        productKey,
        horizonDays,
        disabledModels,
        scaleMode,
        lat,
        lon,
        tz,
        reqId
      );

      return modelMeta;
    }

    this.currentTimeStepMs = state.currentTimeStep ? state.currentTimeStep.getTime() : null;
    const now = new Date();
    // Anchor to top of current UTC hour (matching ManifestTimelineHelper.generateMasterTimelineRange and LegendTrendChartService)
    const nowRoundedMs = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      0, 0, 0
    );
    // Render starting at -8H from "NOW", matching the start of the master date/time slider
    const startMs = nowRoundedMs - 8 * 3600 * 1000;
    const { timestamps } = this.buildHourlyGrid(startMs, horizonDays);
    this.currentTimestamps = timestamps;
    const labels = this.formatLabels(timestamps, tz, isImperial);

    // Fetch binary forecasts in parallel
    const forecastPromises = modelsList.map(async m => {
      const cacheKey = `${lat.toFixed(4)},${lon.toFixed(4)}:${m.id.toLowerCase()}`;
      if (this.cachedForecasts.has(cacheKey)) {
        return { model: m, forecast: this.cachedForecasts.get(cacheKey) || null };
      }
      try {
        const fc = await ForecastBinaryService.getForecastForPoint(lat, lon, state.manifest!, m.id);
        if (fc) {
          this.cachedForecasts.set(cacheKey, fc);
        }
        return { model: m, forecast: fc };
      } catch (err) {
        console.warn(`[ModelComparison] Failed to load forecast for ${m.id}:`, err);
        return { model: m, forecast: null };
      }
    });

    const results = await Promise.all(forecastPromises);
    if (this.pendingRequestId !== reqId || !container.isConnected) return [];

    const modelMeta: ModelMetaInfo[] = results.map(r => {
      const maxH = this.getModelMaxHours(r.model.id, r.forecast);
      const isAvail = maxH >= 12; // At least basic initial hours available
      return {
        id: r.model.id,
        label: r.model.label,
        color: this.getModelColor(r.model.id),
        maxHours: maxH,
        isAvailableInHorizon: isAvail
      };
    });

    container.innerHTML = '';
    const hasAnyLoadedModel = results.some(r => Boolean(r.forecast && r.forecast.numHours > 0));
    const userTier = TierAccessService.getUserAccessTier(state);
    const reqTier = getRequiredTierForLayer('forecast_point_tiles');
    if (!hasAnyLoadedModel && (ForecastBinaryService.lastAccessDenial || !hasTierAccess(userTier, reqTier))) {
      ephemerisContainer.innerHTML = '';
      container.innerHTML = TierAccessService.buildTrendChartDenialHtml({
        denial: ForecastBinaryService.lastAccessDenial,
        requiredTier: reqTier,
        featureLabel: 'multi-model comparison trend charts',
      });
      return modelMeta;
    }

    const isConsensus = !!state.modelComparisonConsensus;

    if (mode === 'elevation' && (productKey === 'hrtemp' || productKey === 'winds')) {
      await this.renderElevationModeCharts(container, results, timestamps, labels, tz, isImperial, disabledModels, lat, lon, poi.elev, scaleMode, isConsensus, productKey);
    } else if (mode === 'meteogram') {
      this.renderMeteogramModeCharts(container, results, timestamps, labels, tz, isImperial, disabledModels, scaleMode, isConsensus);
    } else if (productKey === 'precip') {
      this.renderPrecipComboChart(container, results, timestamps, labels, tz, isImperial, disabledModels, scaleMode, isConsensus);
    } else if (productKey === 'precip_daily') {
      this.renderDailyPrecipAccumChart(container, results, timestamps, tz, isImperial, disabledModels, horizonDays);
    } else {
      this.renderSingleProductChart(container, results, timestamps, labels, tz, isImperial, productKey, disabledModels, scaleMode, isConsensus);
    }

    // Render ephemeris ribbon
    if (productKey !== 'precip_daily') {
      this.renderEphemerisRibbon(ephemerisContainer, timestamps, tz, lat, lon);
    } else {
      ephemerisContainer.innerHTML = '';
    }

    return modelMeta;
  }

  private static renderSingleProductChart(
    container: HTMLElement,
    results: { model: { id: string; label: string }; forecast: DecodedPointForecast | null }[],
    timestamps: number[],
    labels: string[],
    tz: string,
    isImperial: boolean,
    productKey: string,
    disabledModels: Set<string>,
    scaleMode: 'auto' | 'fixed',
    isConsensus: boolean = false
  ): void {
    const isProprietary = ['darkskies', 'optics_dew_risk', 'lightning', 'thunderstorm'].includes(productKey);

    // Proprietary Visual Score Guide Ribbon
    if (isProprietary) {
      const guideEl = document.createElement('div');
      guideEl.className = 'mc-proprietary-legend';
      if (productKey === 'darkskies') {
        guideEl.innerHTML = `
          <span class="mc-legend-guide-title">SkySkout Stargazing Scale:</span>
          <span class="mc-legend-guide-tier min">0 (Min: Obscured / Overcast)</span>
          <div class="mc-legend-guide-bar"></div>
          <span class="mc-legend-guide-tier mid">5 (Fair Viewing)</span>
          <div class="mc-legend-guide-bar"></div>
          <span class="mc-legend-guide-tier max">10 (Max: Pristine Bortle 1)</span>
        `;
      } else if (productKey === 'optics_dew_risk') {
        guideEl.innerHTML = `
          <span class="mc-legend-guide-title">Optics Dew Risk Scale:</span>
          <span class="mc-legend-guide-tier min" style="color: #34d399;">0% (Min Risk: Dry Optics)</span>
          <div class="mc-legend-guide-bar" style="background: linear-gradient(to right, #10b981 0%, #38bdf8 40%, #f59e0b 70%, #ef4444 100%);"></div>
          <span class="mc-legend-guide-tier mid">50% (Moderate Risk)</span>
          <div class="mc-legend-guide-bar" style="background: linear-gradient(to right, #10b981 0%, #38bdf8 40%, #f59e0b 70%, #ef4444 100%);"></div>
          <span class="mc-legend-guide-tier max" style="color: #f87171;">100% (Max: Severe Dew / Fog)</span>
        `;
      } else if (productKey === 'lightning' || productKey === 'thunderstorm') {
        guideEl.innerHTML = `
          <span class="mc-legend-guide-title">Convective Hazard Scale:</span>
          <span class="mc-legend-guide-tier min" style="color: #34d399;">0 (Min: Minimal Hazard)</span>
          <div class="mc-legend-guide-bar" style="background: linear-gradient(to right, #10b981 0%, #f59e0b 50%, #ef4444 100%);"></div>
          <span class="mc-legend-guide-tier mid">50 (Moderate Hazard)</span>
          <div class="mc-legend-guide-bar" style="background: linear-gradient(to right, #10b981 0%, #f59e0b 50%, #ef4444 100%);"></div>
          <span class="mc-legend-guide-tier max" style="color: #f87171;">100 (Max: Extreme Hazard)</span>
        `;
      }
      container.appendChild(guideEl);
    }

    const chartWrap = document.createElement('div');
    chartWrap.className = 'model-comparison-canvas-wrap';
    chartWrap.style.cssText = 'position: relative; width: 100%; height: 100%; min-height: 0; overflow: hidden;';
    const canvas = document.createElement('canvas');
    canvas.id = 'model-comparison-single-chart';
    canvas.style.cssText = 'position: absolute; left: 0; top: 0; width: 100% !important; height: 100% !important;';
    chartWrap.appendChild(canvas);
    container.appendChild(chartWrap);

    let unit = '';
    const datasets: any[] = [];
    let minVal = Infinity;
    let maxVal = -Infinity;

    for (const r of results) {
      if (!r.forecast || r.forecast.numHours === 0) continue;
      if (disabledModels.has(r.model.id.toLowerCase())) continue;

      const rawOffsets = r.forecast.rawHourOffsets || getRawHourOffsetsForModel(r.model.id, r.forecast.numHours);
      const productData = this.extractProductSeries(r.forecast, productKey, isImperial);
      unit = productData.unit;
      const resampled = this.resampleSeries(rawOffsets, productData.values, r.forecast.cycleEpoch, timestamps);

      for (const val of resampled) {
        if (val !== null && typeof val === 'number') {
          if (val < minVal) minVal = val;
          if (val > maxVal) maxVal = val;
        }
      }

      const color = this.getModelColor(r.model.id);
      datasets.push({
        label: `${r.model.label} (${r.model.id.toUpperCase()})`,
        data: resampled,
        borderColor: color,
        backgroundColor: color + '18',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.3,
        spanGaps: false
      });
    }

    if (isConsensus && datasets.length >= 2) {
      const decimals = productKey === 'precip' ? 2 : 1;
      this.applyConsensusToDatasets(datasets, timestamps.length, decimals);
    }

    if (datasets.length === 0) {
      const emptyNotice = document.createElement('div');
      emptyNotice.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #94a3b8; font-size: 0.85rem;';
      emptyNotice.textContent = 'No forecast model data currently available for selected location.';
      chartWrap.appendChild(emptyNotice);
      return;
    }

    if (minVal === Infinity) { minVal = 0; maxVal = 100; }

    // Calculate snug Auto-scale bounds vs Fixed scale bounds
    let yMin: number | undefined = undefined;
    let yMax: number | undefined = undefined;
    let yStepSize: number | undefined = undefined;

    if (scaleMode === 'auto') {
      if (productKey === 'hrtemp' || productKey === 'windchill' || productKey === 'dewpoint') {
        yMin = Math.floor(minVal - 2);
        yMax = Math.ceil(maxVal + 2);
      } else if (productKey === 'clouds' || productKey === 'relative_humidity' || productKey === 'precip_prob') {
        yMin = Math.max(0, Math.floor(minVal - 5));
        yMax = Math.min(100, Math.ceil(maxVal + 5));
      } else if (productKey === 'winds') {
        yMin = 0;
        yMax = Math.ceil(maxVal * 1.15 + 2);
      } else if (productKey === 'precip') {
        yMin = 0;
        yMax = Math.max(0.1, Math.ceil(maxVal * 1.25 * 100) / 100);
      } else if (isProprietary) {
        yMin = Math.max(0, Math.floor(minVal - 5));
        yMax = Math.min(100, Math.ceil(maxVal + 5));
      } else if (productKey === 'freezing_level') {
        yMin = Math.max(0, Math.floor(minVal - 500));
        yMax = Math.ceil(maxVal + 500);
      }
    } else {
      // Fixed standard scale
      if (productKey === 'hrtemp' || productKey === 'windchill' || productKey === 'dewpoint') {
        if (isImperial) {
          yMin = Math.min(0, Math.floor(minVal / 10) * 10);
          yMax = Math.max(100, Math.ceil(maxVal / 10) * 10);
        } else {
          yMin = Math.min(-20, Math.floor(minVal / 5) * 5);
          yMax = Math.max(40, Math.ceil(maxVal / 5) * 5);
        }
      } else if (productKey === 'darkskies') {
        yMin = 0;
        yMax = 10;
        yStepSize = 2;
      } else if (productKey === 'clouds' || productKey === 'relative_humidity' || productKey === 'precip_prob' || isProprietary) {
        yMin = 0;
        yMax = 100;
        yStepSize = 25;
      } else if (productKey === 'winds') {
        yMin = 0;
        yMax = Math.max(40, Math.ceil(maxVal / 10) * 10);
      } else if (productKey === 'precip') {
        yMin = 0;
        yMax = Math.max(1.0, Math.ceil(maxVal * 1.2 * 10) / 10);
      } else if (productKey === 'freezing_level') {
        yMin = 0;
        yMax = Math.max(15000, Math.ceil(maxVal / 1000) * 1000);
      }
    }

    const yTitleText = productKey === 'darkskies' ? 'Stargazing Score (0 - 10)'
      : productKey === 'optics_dew_risk' ? 'Optics Dew Risk (%)'
        : (productKey === 'lightning' || productKey === 'thunderstorm') ? 'Thunderstorm Probability (%)'
          : isProprietary ? 'Score (0 - 100)'
            : '';

    const themeColors = ModelComparisonChartService.getThemeColors();
    const timeTicksMap = LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial);

    const chart = new Chart(canvas, {
      type: 'line',
      data: { labels, datasets },
      plugins: [
        ModelComparisonChartService.createCursorPlugin(),
        LegendTrendChartService.createTimeTickMarksPlugin(`mc_${productKey}`, timeTicksMap)
      ],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        interaction: { mode: 'index', intersect: false },
        layout: {
          padding: {
            top: 4,
            bottom: 2,
            left: 2,
            right: 8
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const val = ctx.parsed.y;
                if (val === null || val === undefined || isNaN(val)) return ` ${ctx.dataset.label}: Unavail`;
                if (isProprietary) {
                  const maxDenominator = productKey === 'darkskies' ? '10' : '100';
                  return ` ${ctx.dataset.label}: ${val} / ${maxDenominator} (${ModelComparisonChartService.getScoreQualitativeTier(val, productKey)})`;
                }
                return ` ${ctx.dataset.label}: ${val} ${unit}`;
              }
            }
          }
        },
        scales: {
          x: LegendTrendChartService.getCommonXScale(timeTicksMap),
          y: {
            min: yMin,
            max: yMax,
            grid: { color: themeColors.gridColor },
            title: {
              display: isProprietary,
              text: yTitleText,
              color: themeColors.titleColor,
              font: { size: 9, weight: 'bold' }
            },
            ticks: {
              color: themeColors.tickColor,
              font: { size: 9 },
              maxTicksLimit: 6,
              stepSize: yStepSize,
              callback: (v: any) => {
                const num = Number(v);
                if (isProprietary) {
                  if (productKey === 'darkskies') {
                    if (num >= 9.5) return `${num} (Max - Pristine)`;
                    if (num >= 7 && num <= 8) return `${num} (Great)`;
                    if (num >= 5 && num <= 6) return `${num} (Fair)`;
                    if (num >= 2 && num <= 3) return `${num} (Poor)`;
                    if (num <= 0.5) return `${num} (Min - Obscured)`;
                    return `${num}`;
                  }
                  if (num >= 95 || (scaleMode === 'auto' && yMax !== undefined && num >= yMax - 2)) {
                    if (productKey === 'optics_dew_risk') return `${num} (Max - Severe Risk)`;
                    if (productKey === 'lightning' || productKey === 'thunderstorm') return `${num} (Max - Extreme)`;
                    return `${num} (Max)`;
                  }
                  if (num >= 70 && num <= 80) {
                    if (productKey === 'optics_dew_risk') return `${num} (High Risk)`;
                    if (productKey === 'lightning' || productKey === 'thunderstorm') return `${num} (High)`;
                    return `${num}`;
                  }
                  if (num >= 45 && num <= 55) {
                    if (productKey === 'optics_dew_risk') return `${num} (Moderate)`;
                    if (productKey === 'lightning' || productKey === 'thunderstorm') return `${num} (Moderate)`;
                    return `${num}`;
                  }
                  if (num >= 20 && num <= 30) {
                    if (productKey === 'optics_dew_risk') return `${num} (Low Risk)`;
                    if (productKey === 'lightning' || productKey === 'thunderstorm') return `${num} (Low)`;
                    return `${num}`;
                  }
                  if (num <= 5 || (scaleMode === 'auto' && yMin !== undefined && num <= yMin + 2)) {
                    if (productKey === 'optics_dew_risk') return `${num} (Min - Dry / Safe)`;
                    if (productKey === 'lightning' || productKey === 'thunderstorm') return `${num} (Min - None)`;
                    return `${num} (Min)`;
                  }
                  return `${num}`;
                }
                if (productKey === 'hrtemp' || productKey === 'windchill') return `${num}°`;
                return `${num}${unit ? ' ' + unit : ''}`;
              }
            }
          }
        }
      }
    });

    this.activeCharts.push(chart);
    requestAnimationFrame(() => {
      this.resizeAll();
    });
  }

  private static renderPrecipComboChart(
    container: HTMLElement,
    results: { model: { id: string; label: string }; forecast: DecodedPointForecast | null }[],
    timestamps: number[],
    labels: string[],
    tz: string,
    isImperial: boolean,
    disabledModels: Set<string>,
    scaleMode: 'auto' | 'fixed',
    isConsensus: boolean = false
  ): void {
    const chartWrap = document.createElement('div');
    chartWrap.className = 'model-comparison-canvas-wrap';
    chartWrap.style.cssText = 'position: relative; width: 100%; height: 100%; min-height: 0; overflow: hidden;';
    const canvas = document.createElement('canvas');
    canvas.id = 'model-comparison-precip-combo-chart';
    canvas.style.cssText = 'position: absolute; left: 0; top: 0; width: 100% !important; height: 100% !important;';
    chartWrap.appendChild(canvas);
    container.appendChild(chartWrap);

    const rateUnit = isImperial ? 'in/h' : 'mm/h';
    const datasets: any[] = [];
    let maxRate = 0;

    for (const r of results) {
      if (!r.forecast || r.forecast.numHours === 0) continue;
      if (disabledModels.has(r.model.id.toLowerCase())) continue;

      const rawOffsets = r.forecast.rawHourOffsets || getRawHourOffsetsForModel(r.model.id, r.forecast.numHours);

      // Precip rate: in/h or mm/h
      const rateVals = isImperial
        ? r.forecast.precipRateMmH.map(v => Math.round((v / 25.4) * 100) / 100)
        : r.forecast.precipRateMmH;
      const resampledRate = this.resampleSeries(rawOffsets, rateVals, r.forecast.cycleEpoch, timestamps);

      // Precip probability: 0-100%
      const popVals = r.forecast.precipProb;
      const resampledPop = this.resampleSeries(rawOffsets, popVals, r.forecast.cycleEpoch, timestamps);

      for (const val of resampledRate) {
        if (val !== null && typeof val === 'number' && val > maxRate) {
          maxRate = val;
        }
      }

      const color = this.getModelColor(r.model.id);

      // 1. Grouped Bar for Precip Rate (Left Axis)
      datasets.push({
        type: 'bar',
        label: `${r.model.label} (${r.model.id.toUpperCase()}) Rate`,
        data: resampledRate,
        yAxisID: 'y',
        backgroundColor: color + '55',
        borderColor: color,
        borderWidth: 1.5,
        borderRadius: 2,
        order: 2
      });

      // 2. Line for PoP % (Right Axis)
      datasets.push({
        type: 'line',
        label: `${r.model.label} (${r.model.id.toUpperCase()}) PoP`,
        data: resampledPop,
        yAxisID: 'y1',
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
        order: 1
      });
    }

    const themeColors = ModelComparisonChartService.getThemeColors();
    const isLight = themeService.isLightPalette();

    if (isConsensus && datasets.length >= 4) {
      const numSteps = timestamps.length;
      const consensusRate: (number | null)[] = new Array(numSteps);
      const consensusPop: (number | null)[] = new Array(numSteps);

      for (let t = 0; t < numSteps; t++) {
        let sumRate = 0, countRate = 0;
        let sumPop = 0, countPop = 0;
        for (const ds of datasets) {
          if (ds.data && ds.data[t] !== null && typeof ds.data[t] === 'number' && !isNaN(ds.data[t])) {
            if (ds.yAxisID === 'y') {
              sumRate += ds.data[t];
              countRate++;
            } else if (ds.yAxisID === 'y1') {
              sumPop += ds.data[t];
              countPop++;
            }
          }
        }
        consensusRate[t] = countRate > 0 ? Math.round((sumRate / countRate) * 100) / 100 : null;
        consensusPop[t] = countPop > 0 ? Math.round(sumPop / countPop) : null;
      }

      // Fade existing model datasets
      for (const ds of datasets) {
        if (ds.yAxisID === 'y') {
          ds.backgroundColor = 'rgba(148, 163, 184, 0.15)';
          ds.borderColor = 'rgba(148, 163, 184, 0.25)';
          ds.borderWidth = 1;
        } else {
          const origColor = ds.borderColor;
          ds.borderWidth = 1.2;
          ds.pointHoverRadius = 2;
          if (typeof origColor === 'string' && origColor.startsWith('#') && origColor.length === 7) {
            ds.borderColor = origColor + '40';
          }
        }
      }

      // Prepend consensus datasets
      datasets.unshift({
        type: 'line',
        label: '✨ Consensus PoP',
        data: consensusPop,
        yAxisID: 'y1',
        borderColor: themeColors.consensusColor,
        backgroundColor: 'transparent',
        borderWidth: 3.5,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 6,
        order: 0
      });

      datasets.unshift({
        type: 'bar',
        label: '✨ Consensus Rate',
        data: consensusRate,
        yAxisID: 'y',
        backgroundColor: isLight ? 'rgba(2, 132, 199, 0.45)' : 'rgba(56, 189, 248, 0.45)',
        borderColor: themeColors.consensusColor,
        borderWidth: 2,
        borderRadius: 2,
        order: 0
      });
    }

    if (datasets.length === 0) {
      const emptyNotice = document.createElement('div');
      emptyNotice.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #94a3b8; font-size: 0.85rem;';
      emptyNotice.textContent = 'No precipitation forecast model data currently available for selected location.';
      chartWrap.appendChild(emptyNotice);
      return;
    }

    const yMaxRate = scaleMode === 'auto'
      ? Math.max(isImperial ? 0.1 : 2.5, Math.ceil(maxRate * 1.3 * 100) / 100)
      : (isImperial ? 0.75 : 20.0);

    const timeTicksMap = LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial);

    const chart = new Chart(canvas, {
      data: {
        labels,
        datasets
      },
      plugins: [
        ModelComparisonChartService.createCursorPlugin(),
        LegendTrendChartService.createTimeTickMarksPlugin('mc_precip_combo', timeTicksMap)
      ],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.94)',
            borderColor: 'rgba(255, 255, 255, 0.15)',
            borderWidth: 1,
            titleColor: '#e2e8f0',
            bodyColor: '#cbd5e1',
            padding: 8,
            boxPadding: 4,
            callbacks: {
              label: (context) => {
                const ds = context.dataset as any;
                const val = context.parsed.y;
                if (val === null || val === undefined || isNaN(val)) return '';
                if (ds.yAxisID === 'y1') {
                  return `  ${ds.label}: ${Math.round(val)}%`;
                } else {
                  return `  ${ds.label}: ${val} ${rateUnit}`;
                }
              }
            }
          }
        },
        scales: {
          x: LegendTrendChartService.getCommonXScale(timeTicksMap),
          y: {
            type: 'linear',
            position: 'left',
            min: 0,
            max: yMaxRate,
            title: {
              display: true,
              text: `Precip Rate (${rateUnit})`,
              color: themeColors.titleColor,
              font: { size: 10, weight: 'bold' }
            },
            grid: {
              color: themeColors.gridColor
            },
            ticks: {
              color: themeColors.tickColor,
              font: { size: 9 },
              callback: (v) => isImperial ? `${Number(v).toFixed(2)}"` : `${v}mm`
            }
          },
          y1: {
            type: 'linear',
            position: 'right',
            min: 0,
            max: 100,
            title: {
              display: true,
              text: 'Precip Probability (%)',
              color: themeColors.titleColor,
              font: { size: 10, weight: 'bold' }
            },
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              color: themeColors.tickColor,
              font: { size: 9 },
              callback: (v) => `${v}%`
            }
          }
        }
      }
    });

    this.activeCharts.push(chart);
    requestAnimationFrame(() => {
      this.resizeAll();
    });
  }

  private static renderDailyPrecipAccumChart(
    container: HTMLElement,
    results: { model: { id: string; label: string }; forecast: DecodedPointForecast | null }[],
    timestamps: number[],
    tz: string,
    isImperial: boolean,
    disabledModels: Set<string>,
    horizonDays: number
  ): void {
    const rainUnit = isImperial ? 'in' : 'mm';
    const snowUnit = isImperial ? 'in' : 'cm';

    // Group timestamps by local calendar day (YYYY-MM-DD in tz)
    const dayMap = new Map<string, { label: string; indices: number[] }>();
    const now = new Date();
    const todayStartMs = this.getLocalDayStartUtcMs(now, tz);

    for (let dayOffset = 0; dayOffset < horizonDays; dayOffset++) {
      const d = new Date(todayStartMs + dayOffset * 86400000 + 43200000);
      const dateStr = d.toLocaleDateString('en-CA', { timeZone: tz });
      const isToday = dayOffset === 0;
      const wDay = d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz });
      const label = isToday ? `Today (${wDay})` : wDay;
      dayMap.set(dateStr, { label, indices: [] });
    }

    timestamps.forEach((ts, idx) => {
      const d = new Date(ts);
      const dateStr = d.toLocaleDateString('en-CA', { timeZone: tz });
      if (dayMap.has(dateStr)) {
        dayMap.get(dateStr)!.indices.push(idx);
      }
    });

    const dayKeys = Array.from(dayMap.keys());
    const dayLabels = dayKeys.map(k => dayMap.get(k)!.label);

    const gridContainer = document.createElement('div');
    gridContainer.className = 'mc-daily-container';
    container.appendChild(gridContainer);

    const activeModels = results.filter(r => r.forecast && r.forecast.numHours > 0 && !disabledModels.has(r.model.id.toLowerCase()));

    // Card 1: Liquid Rain Accumulation
    const rainCard = document.createElement('div');
    rainCard.className = 'mc-daily-card';
    rainCard.innerHTML = `
      <div class="mc-daily-header">
        <span>🌧️ Daily Liquid Precipitation</span>
        <span style="font-size: 0.65rem; color: #94a3b8; font-weight: normal;">Total Rain (${rainUnit})</span>
      </div>
      <div class="mc-daily-canvas-wrap">
        <canvas id="mc-daily-rain-canvas"></canvas>
      </div>
    `;
    gridContainer.appendChild(rainCard);

    // Card 2: Snowfall Accumulation
    const snowCard = document.createElement('div');
    snowCard.className = 'mc-daily-card';
    snowCard.innerHTML = `
      <div class="mc-daily-header">
        <span>❄️ Daily Snowfall Accumulation</span>
        <span style="font-size: 0.65rem; color: #94a3b8; font-weight: normal;">Total Snow (${snowUnit})</span>
      </div>
      <div class="mc-daily-canvas-wrap">
        <canvas id="mc-daily-snow-canvas"></canvas>
      </div>
    `;
    gridContainer.appendChild(snowCard);

    // Prepare datasets for rain and snow
    const rainDatasets: any[] = [];
    const snowDatasets: any[] = [];

    for (const r of activeModels) {
      const fc = r.forecast!;
      const rawOffsets = fc.rawHourOffsets || getRawHourOffsetsForModel(r.model.id, fc.numHours);

      const hourlyRain = isImperial
        ? fc.precipRateMmH.map(v => v / 25.4)
        : fc.precipRateMmH;
      const resampledRain = this.resampleSeries(rawOffsets, hourlyRain, fc.cycleEpoch, timestamps);

      const hourlySnow = isImperial
        ? fc.snowRateCmH.map(v => v / 2.54)
        : fc.snowRateCmH;
      const resampledSnow = this.resampleSeries(rawOffsets, hourlySnow, fc.cycleEpoch, timestamps);

      const dailyRainTotals: number[] = [];
      const dailySnowTotals: number[] = [];

      for (const k of dayKeys) {
        const indices = dayMap.get(k)!.indices;
        let rSum = 0;
        let sSum = 0;
        for (const idx of indices) {
          const rVal = resampledRain[idx];
          if (rVal !== null && typeof rVal === 'number') rSum += rVal;
          const sVal = resampledSnow[idx];
          if (sVal !== null && typeof sVal === 'number') sSum += sVal;
        }
        dailyRainTotals.push(Math.round(rSum * 100) / 100);
        dailySnowTotals.push(Math.round(sSum * 10) / 10);
      }

      const color = this.getModelColor(r.model.id);

      rainDatasets.push({
        label: `${r.model.id.toUpperCase()}`,
        data: dailyRainTotals,
        backgroundColor: color + 'bb',
        borderColor: color,
        borderWidth: 1.5,
        borderRadius: 4
      });

      snowDatasets.push({
        label: `${r.model.id.toUpperCase()}`,
        data: dailySnowTotals,
        backgroundColor: color + 'bb',
        borderColor: color,
        borderWidth: 1.5,
        borderRadius: 4
      });
    }

    // Instantiate Rain Chart
    const rainCanvas = rainCard.querySelector('#mc-daily-rain-canvas') as HTMLCanvasElement;
    if (rainCanvas) {
      const rainChart = new Chart(rainCanvas, {
        type: 'bar',
        data: { labels: dayLabels, datasets: rainDatasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.94)',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              titleColor: '#e2e8f0',
              bodyColor: '#cbd5e1',
              callbacks: {
                label: (ctx) => `  ${ctx.dataset.label}: ${ctx.parsed.y} ${rainUnit}`
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(255, 255, 255, 0.05)' },
              ticks: { color: '#94a3b8', font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(255, 255, 255, 0.06)' },
              ticks: {
                color: '#94a3b8',
                font: { size: 9 },
                callback: (v) => `${v} ${rainUnit}`
              }
            }
          }
        }
      });
      this.activeCharts.push(rainChart);
    }

    // Instantiate Snow Chart
    const snowCanvas = snowCard.querySelector('#mc-daily-snow-canvas') as HTMLCanvasElement;
    if (snowCanvas) {
      const snowChart = new Chart(snowCanvas, {
        type: 'bar',
        data: { labels: dayLabels, datasets: snowDatasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.94)',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              titleColor: '#e2e8f0',
              bodyColor: '#cbd5e1',
              callbacks: {
                label: (ctx) => `  ${ctx.dataset.label}: ${ctx.parsed.y} ${snowUnit}`
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(255, 255, 255, 0.05)' },
              ticks: { color: '#94a3b8', font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(255, 255, 255, 0.06)' },
              ticks: {
                color: '#94a3b8',
                font: { size: 9 },
                callback: (v) => `${v} ${snowUnit}`
              }
            }
          }
        }
      });
      this.activeCharts.push(snowChart);
    }

    requestAnimationFrame(() => {
      this.resizeAll();
    });
  }

  private static async renderElevationModeCharts(
    container: HTMLElement,
    results: { model: { id: string; label: string }; forecast: DecodedPointForecast | null }[],
    timestamps: number[],
    labels: string[],
    tz: string,
    isImperial: boolean,
    disabledModels: Set<string>,
    lat: number,
    lon: number,
    poiElev: number,
    scaleMode: 'auto' | 'fixed',
    isConsensus: boolean = false,
    productKey: string = 'hrtemp'
  ): Promise<void> {
    const profile = await getElevationAt(lat, lon);
    const minElev = profile.minElevation;
    const maxElev = profile.maxElevation;
    const isWinds = productKey === 'winds';
    const unit = isWinds ? (isImperial ? 'mph' : 'km/h') : (isImperial ? '°F' : '°C');
    const subTitle = isWinds ? 'Vertical Wind Gradient & Boundary Layer Profile' : 'Lapse-Rate & Sounding Profile';

    const formatElev = (ft: number) => isImperial ? `${Math.round(ft)}'` : `${Math.round(ft * 0.3048)}m`;

    const tiers = [
      { id: 'peak', name: `Summit Peak (~${formatElev(maxElev)})`, targetElev: maxElev, color: '#f43f5e' },
      { id: 'poi', name: `Selected POI (~${formatElev(poiElev)})`, targetElev: poiElev, color: '#38bdf8' },
      { id: 'valley', name: `Valley Floor (~${formatElev(minElev)})`, targetElev: minElev, color: '#34d399' }
    ];

    const timeTicksMap = LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial);
    const commonXScale = LegendTrendChartService.getCommonXScale(timeTicksMap);

    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '6px';
    container.style.height = '100%';
    container.style.overflow = 'hidden';

    // Phase 1: Mount all DOM containers simultaneously so flexbox computes equal space
    const tierElements = tiers.map(tier => {
      const tierContainer = document.createElement('div');
      tierContainer.style.cssText = [
        'flex: 1 1 0;',
        'min-height: 0;',
        'max-height: 100%;',
        'display: flex;',
        'flex-direction: column;',
        'background: rgba(15, 23, 42, 0.45);',
        'border: 1px solid rgba(255, 255, 255, 0.08);',
        'border-radius: 6px;',
        'padding: 3px 8px;',
        'overflow: hidden;',
        'box-sizing: border-box;'
      ].join(' ');

      const tierHeader = document.createElement('div');
      tierHeader.style.cssText = [
        'display: flex;',
        'justify-content: space-between;',
        'align-items: center;',
        'font-size: 0.72rem;',
        'color: #94a3b8;',
        'font-weight: 600;',
        'flex-shrink: 0;',
        'height: 16px;',
        'line-height: 16px;',
        'margin-bottom: 2px;'
      ].join(' ');
      tierHeader.innerHTML = `<span>${tier.name}</span><span style="color: ${tier.color}; font-size: 0.66rem;">${subTitle}</span>`;
      tierContainer.appendChild(tierHeader);

      const canvasWrap = document.createElement('div');
      canvasWrap.style.cssText = [
        'flex: 1 1 0;',
        'min-height: 0;',
        'position: relative;',
        'width: 100%;',
        'height: 100%;',
        'overflow: hidden;'
      ].join(' ');

      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position: absolute; left: 0; top: 0; width: 100% !important; height: 100% !important;';
      canvasWrap.appendChild(canvas);
      tierContainer.appendChild(canvasWrap);
      container.appendChild(tierContainer);

      return { tier, canvas };
    });

    // Phase 2: Instantiate charts within mounted, strictly bounded cards
    for (const { tier, canvas } of tierElements) {
      const datasets: any[] = [];
      let minVal = Infinity;
      let maxVal = -Infinity;

      for (const r of results) {
        if (!r.forecast || r.forecast.numHours === 0) continue;
        if (disabledModels.has(r.model.id.toLowerCase())) continue;

        const fc = r.forecast;
        const rawOffsets = fc.rawHourOffsets || getRawHourOffsetsForModel(r.model.id, fc.numHours);

        let targetValues: number[];
        if (isWinds) {
          const sfcWindsMph = fc.windSpeedMph;
          const elevationWindsMph: number[] = new Array(fc.numHours);
          for (let h = 0; h < fc.numHours; h++) {
            const sfcW = sfcWindsMph[h] ?? 0;
            let lvlWinds: Record<number, number> | undefined;
            let lvlHgt: Record<number, number> | undefined;

            if (fc.hasSounding && fc.soundingWindSpeedMph && fc.soundingHeightsM) {
              lvlWinds = {};
              lvlHgt = {};
              for (const lev of [925, 850, 700, 500, 300]) {
                if (fc.soundingWindSpeedMph[lev] && fc.soundingHeightsM[lev]) {
                  lvlWinds[lev] = fc.soundingWindSpeedMph[lev][h];
                  lvlHgt[lev] = fc.soundingHeightsM[lev][h];
                }
              }
            }

            const interp = interpolateSoundingWind(tier.targetElev, poiElev, sfcW, lvlWinds, lvlHgt);
            elevationWindsMph[h] = interp.windSpeedMph;
          }
          targetValues = isImperial
            ? elevationWindsMph
            : elevationWindsMph.map(v => Math.round(v * 1.60934));
        } else {
          const sfcTempsC = fc.tempsC;
          const elevationTempsC: number[] = new Array(fc.numHours);
          for (let h = 0; h < fc.numHours; h++) {
            const sfcT = sfcTempsC[h];
            let lvlTemps: Record<number, number> | undefined;
            let lvlHgt: Record<number, number> | undefined;

            if (fc.hasSounding && fc.soundingTempsC && fc.soundingHeightsM) {
              lvlTemps = {};
              lvlHgt = {};
              for (const lev of [925, 850, 700, 500, 300]) {
                if (fc.soundingTempsC[lev] && fc.soundingHeightsM[lev]) {
                  lvlTemps[lev] = fc.soundingTempsC[lev][h];
                  lvlHgt[lev] = fc.soundingHeightsM[lev][h];
                }
              }
            }

            const interp = interpolateSoundingTemp(tier.targetElev, poiElev, sfcT, lvlTemps, lvlHgt);
            elevationTempsC[h] = interp.tempC;
          }
          targetValues = isImperial ? elevationTempsC.map(c => Math.round(c * 1.8 + 32)) : elevationTempsC.map(c => Math.round(c));
        }

        for (const v of targetValues) {
          if (v !== null && typeof v === 'number') {
            if (v < minVal) minVal = v;
            if (v > maxVal) maxVal = v;
          }
        }

        const resampled = this.resampleSeries(rawOffsets, targetValues, fc.cycleEpoch, timestamps);
        const color = this.getModelColor(r.model.id);

        datasets.push({
          label: `${r.model.label}`,
          data: resampled,
          borderColor: color,
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.3,
          spanGaps: false
        });
      }

      if (isConsensus && datasets.length >= 2) {
        this.applyConsensusToDatasets(datasets, timestamps.length, 1);
      }

      if (minVal === Infinity) {
        if (isWinds) {
          minVal = 0; maxVal = isImperial ? 25 : 40;
        } else {
          minVal = 32; maxVal = 70;
        }
      }

      let yMin: number | undefined = undefined;
      let yMax: number | undefined = undefined;
      if (isWinds) {
        if (scaleMode === 'auto') {
          yMin = Math.max(0, Math.floor(minVal - 2));
          yMax = Math.ceil(maxVal + 2);
        } else {
          yMin = 0;
          yMax = Math.max(isImperial ? 40 : 60, Math.ceil(maxVal / 10) * 10);
        }
      } else {
        if (scaleMode === 'auto') {
          yMin = Math.floor(minVal - 2);
          yMax = Math.ceil(maxVal + 2);
        } else {
          if (isImperial) {
            yMin = Math.min(0, Math.floor(minVal / 10) * 10);
            yMax = Math.max(100, Math.ceil(maxVal / 10) * 10);
          } else {
            yMin = Math.min(-20, Math.floor(minVal / 5) * 5);
            yMax = Math.max(40, Math.ceil(maxVal / 5) * 5);
          }
        }
      }

      // Add Freezing Reference Level (32°F / 0°C line) if relevant to temperature range
      if (!isWinds) {
        const freezeLine = isImperial ? 32 : 0;
        const nearFreezingBuffer = isImperial ? 10 : 6;
        if (minVal <= freezeLine + nearFreezingBuffer && maxVal >= freezeLine - nearFreezingBuffer) {
          datasets.push({
            label: `Freezing (${freezeLine}${unit})`,
            data: new Array(timestamps.length).fill(freezeLine),
            borderColor: 'rgba(56, 189, 248, 0.5)',
            borderWidth: 1,
            borderDash: [4, 4],
            pointRadius: 0,
            fill: false
          });
        }
      }

      const themeColors = ModelComparisonChartService.getThemeColors();

      const chart = new Chart(canvas, {
        type: 'line',
        data: { labels, datasets },
        plugins: [
          ModelComparisonChartService.createCursorPlugin(),
          LegendTrendChartService.createTimeTickMarksPlugin(`mc_elev_${tier.id}`, timeTicksMap)
        ],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 200 },
          interaction: { mode: 'index', intersect: false },
          layout: {
            padding: {
              top: 2,
              bottom: 1,
              left: 2,
              right: 6
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.parsed.y !== null ? (isWinds ? `${ctx.parsed.y} ${unit}` : `${ctx.parsed.y}${unit}`) : 'Unavail'}`
              }
            }
          },
          scales: {
            x: {
              ...commonXScale,
              ticks: {
                ...commonXScale.ticks,
                display: tier.id === 'valley'
              }
            },
            y: {
              min: yMin,
              max: yMax,
              grid: { color: themeColors.gridColor },
              ticks: {
                color: themeColors.tickColor,
                font: { size: 8 },
                maxTicksLimit: 4,
                callback: (v: any) => isWinds ? `${v} ${unit}` : `${v}°`
              }
            }
          }
        }
      });

      this.activeCharts.push(chart);
    }

    requestAnimationFrame(() => {
      this.resizeAll();
    });
  }

  private static renderMeteogramModeCharts(
    container: HTMLElement,
    results: { model: { id: string; label: string }; forecast: DecodedPointForecast | null }[],
    timestamps: number[],
    labels: string[],
    tz: string,
    isImperial: boolean,
    disabledModels: Set<string>,
    scaleMode: 'auto' | 'fixed',
    isConsensus: boolean = false
  ): void {
    const metrics = [
      { key: 'hrtemp', title: 'Temperature', unit: isImperial ? '°F' : '°C' },
      { key: 'clouds', title: 'Cloud Cover', unit: '%' },
      { key: 'precip', title: 'Precip Rate', unit: isImperial ? 'in/h' : 'mm/h' },
      { key: 'winds', title: 'Wind Speed', unit: isImperial ? 'mph' : 'km/h' }
    ];

    const timeTicksMap = LegendTrendChartService.getTimeTickInfo(timestamps, tz, isImperial);
    const commonXScale = LegendTrendChartService.getCommonXScale(timeTicksMap);

    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '5px';
    container.style.height = '100%';
    container.style.overflow = 'hidden';

    // Phase 1: Mount all DOM containers simultaneously
    const metricElements = metrics.map((met, idx) => {
      const metContainer = document.createElement('div');
      metContainer.style.cssText = [
        'flex: 1 1 0;',
        'min-height: 0;',
        'max-height: 100%;',
        'display: flex;',
        'flex-direction: column;',
        'background: rgba(15, 23, 42, 0.45);',
        'border: 1px solid rgba(255, 255, 255, 0.08);',
        'border-radius: 5px;',
        'padding: 2px 8px;',
        'overflow: hidden;',
        'box-sizing: border-box;'
      ].join(' ');

      const metHeader = document.createElement('div');
      metHeader.style.cssText = [
        'display: flex;',
        'justify-content: space-between;',
        'align-items: center;',
        'font-size: 0.68rem;',
        'color: #94a3b8;',
        'font-weight: 600;',
        'flex-shrink: 0;',
        'height: 15px;',
        'line-height: 15px;',
        'margin-bottom: 1px;'
      ].join(' ');
      metHeader.innerHTML = `<span>${met.title}</span><span style="font-size: 0.62rem; color: #64748b;">${met.unit}</span>`;
      metContainer.appendChild(metHeader);

      const canvasWrap = document.createElement('div');
      canvasWrap.style.cssText = [
        'flex: 1 1 0;',
        'min-height: 0;',
        'position: relative;',
        'width: 100%;',
        'height: 100%;',
        'overflow: hidden;'
      ].join(' ');

      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position: absolute; left: 0; top: 0; width: 100% !important; height: 100% !important;';
      canvasWrap.appendChild(canvas);
      metContainer.appendChild(canvasWrap);
      container.appendChild(metContainer);

      return { met, canvas, isLast: idx === metrics.length - 1 };
    });

    const themeColors = ModelComparisonChartService.getThemeColors();

    // Phase 2: Instantiate charts
    for (const { met, canvas, isLast } of metricElements) {
      const datasets: any[] = [];
      let minVal = Infinity;
      let maxVal = -Infinity;

      for (const r of results) {
        if (!r.forecast || r.forecast.numHours === 0) continue;
        if (disabledModels.has(r.model.id.toLowerCase())) continue;

        const rawOffsets = r.forecast.rawHourOffsets || getRawHourOffsetsForModel(r.model.id, r.forecast.numHours);
        const productData = this.extractProductSeries(r.forecast, met.key, isImperial);
        const resampled = this.resampleSeries(rawOffsets, productData.values, r.forecast.cycleEpoch, timestamps);

        for (const val of resampled) {
          if (val !== null && typeof val === 'number') {
            if (val < minVal) minVal = val;
            if (val > maxVal) maxVal = val;
          }
        }

        const color = this.getModelColor(r.model.id);
        datasets.push({
          label: r.model.label,
          data: resampled,
          borderColor: color,
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.3,
          spanGaps: false
        });
      }

      if (isConsensus && datasets.length >= 2) {
        const decimals = met.key === 'precip' ? 2 : 1;
        this.applyConsensusToDatasets(datasets, timestamps.length, decimals);
      }

      if (minVal === Infinity) { minVal = 0; maxVal = 100; }

      let yMin: number | undefined = undefined;
      let yMax: number | undefined = undefined;

      if (scaleMode === 'auto') {
        if (met.key === 'hrtemp') {
          yMin = Math.floor(minVal - 2);
          yMax = Math.ceil(maxVal + 2);
        } else if (met.key === 'clouds') {
          yMin = Math.max(0, Math.floor(minVal - 5));
          yMax = Math.min(100, Math.ceil(maxVal + 5));
        } else if (met.key === 'precip') {
          yMin = 0;
          yMax = Math.max(0.1, Math.ceil(maxVal * 1.25 * 100) / 100);
        } else if (met.key === 'winds') {
          yMin = 0;
          yMax = Math.ceil(maxVal * 1.15 + 2);
        }
      } else {
        if (met.key === 'hrtemp') {
          if (isImperial) {
            yMin = Math.min(0, Math.floor(minVal / 10) * 10);
            yMax = Math.max(100, Math.ceil(maxVal / 10) * 10);
          } else {
            yMin = Math.min(-20, Math.floor(minVal / 5) * 5);
            yMax = Math.max(40, Math.ceil(maxVal / 5) * 5);
          }
        } else if (met.key === 'clouds') {
          yMin = 0;
          yMax = 100;
        } else if (met.key === 'precip') {
          yMin = 0;
          yMax = Math.max(1.0, Math.ceil(maxVal * 1.2 * 10) / 10);
        } else if (met.key === 'winds') {
          yMin = 0;
          yMax = Math.max(40, Math.ceil(maxVal / 10) * 10);
        }
      }

      const chart = new Chart(canvas, {
        type: 'line',
        data: { labels, datasets },
        plugins: [
          ModelComparisonChartService.createCursorPlugin(),
          LegendTrendChartService.createTimeTickMarksPlugin(`mc_meteo_${met.key}`, timeTicksMap)
        ],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 150 },
          interaction: { mode: 'index', intersect: false },
          layout: {
            padding: {
              top: 2,
              bottom: 1,
              left: 2,
              right: 6
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx: any) => ` ${ctx.dataset.label}: ${ctx.parsed.y !== null ? ctx.parsed.y + ' ' + met.unit : 'Unavail'}`
              }
            }
          },
          scales: {
            x: {
              ...commonXScale,
              ticks: {
                ...commonXScale.ticks,
                display: isLast
              }
            },
            y: {
              min: yMin,
              max: yMax,
              grid: { color: themeColors.gridColor },
              ticks: {
                color: themeColors.tickColor,
                font: { size: 8 },
                maxTicksLimit: 4,
                callback: (v: any) => `${v}`
              }
            }
          }
        }
      });

      this.activeCharts.push(chart);
    }

    requestAnimationFrame(() => {
      this.resizeAll();
    });
  }

  private static async renderDailyEventCharts(
    container: HTMLElement,
    state: AppState,
    modelsList: { id: string; label: string }[],
    productKey: string,
    horizonDays: number,
    disabledModels: Set<string>,
    scaleMode: 'auto' | 'fixed',
    lat: number,
    lon: number,
    tz: string,
    reqId: number
  ): Promise<ModelMetaInfo[]> {
    // Determine the two sub-events for the product
    let events: { key: string; label: string; icon: string }[] = [];
    if (productKey === 'sunrise') {
      events = [
        { key: 'sunrise', label: 'Morning Sunrise Vibrancy', icon: '🌅' },
        { key: 'sunset', label: 'Evening Sunset Vibrancy', icon: '🌇' }
      ];
    } else if (productKey === 'goldenhour' || (productKey === 'alpenglow' && state.alpenglowMode === 'goldenhour')) {
      events = [
        { key: 'goldenhour_morning', label: 'Morning Golden Hour Glow', icon: '✨' },
        { key: 'goldenhour_evening', label: 'Evening Golden Hour Glow', icon: '✨' }
      ];
    } else if (productKey === 'alpenglow') {
      events = [
        { key: 'alpenglow_morning', label: 'Morning Alpenglow Peak', icon: '🏔️' },
        { key: 'alpenglow_evening', label: 'Evening Alpenglow Peak', icon: '🏔️' }
      ];
    }

    // Generate day list starting from today in POI timezone
    const now = new Date();
    const todayStartMs = this.getLocalDayStartUtcMs(now, tz);
    const dayLabels: string[] = [];
    const dateStrings: string[] = [];
    for (let i = 0; i < horizonDays; i++) {
      const d = new Date(todayStartMs + i * 86400000 + 43200000);
      dateStrings.push(d.toLocaleDateString('en-CA', { timeZone: tz }));
      const isToday = i === 0;
      const wDay = d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz });
      dayLabels.push(isToday ? `Today (${wDay})` : wDay);
    }

    // Concurrently fetch binary point forecasts for all models in modelsList
    const forecastPromises = modelsList.map(async m => {
      const cacheKey = `${lat.toFixed(4)},${lon.toFixed(4)}:${m.id.toLowerCase()}`;
      if (this.cachedForecasts.has(cacheKey)) {
        return { model: m, forecast: this.cachedForecasts.get(cacheKey) || null };
      }
      try {
        const fc = await ForecastBinaryService.getForecastForPoint(lat, lon, state.manifest!, m.id);
        if (fc) {
          this.cachedForecasts.set(cacheKey, fc);
        }
        return { model: m, forecast: fc };
      } catch (err) {
        console.warn(`[ModelComparison] Failed to load forecast for ${m.id}:`, err);
        return { model: m, forecast: null };
      }
    });

    const results = await Promise.all(forecastPromises);
    if (this.pendingRequestId !== reqId || !container.isConnected) return [];

    const modelMeta: ModelMetaInfo[] = results.map(r => {
      const maxH = this.getModelMaxHours(r.model.id, r.forecast);
      const isAvail = maxH >= 12;
      return {
        id: r.model.id,
        label: r.model.label,
        color: this.getModelColor(r.model.id),
        maxHours: maxH,
        isAvailableInHorizon: isAvail
      };
    });

    const hasAnyLoadedDailyModel = results.some(r => Boolean(r.forecast && r.forecast.numHours > 0));
    const userTier = TierAccessService.getUserAccessTier(state);
    const reqTier = getRequiredTierForLayer('forecast_point_tiles');
    if (!hasAnyLoadedDailyModel && (ForecastBinaryService.lastAccessDenial || !hasTierAccess(userTier, reqTier))) {
      container.innerHTML = TierAccessService.buildTrendChartDenialHtml({
        denial: ForecastBinaryService.lastAccessDenial,
        requiredTier: reqTier,
        featureLabel: 'daily solar & alpenglow model comparison charts',
      });
      return modelMeta;
    }

    // Render Proprietary Score Guide Ribbon at the top
    const guideEl = document.createElement('div');
    guideEl.className = 'mc-proprietary-legend';
    guideEl.innerHTML = `
      <span class="mc-legend-guide-title">SkySkout Vibrancy Scale:</span>
      <span class="mc-legend-guide-tier min">0 (Min: Obscured / Dull)</span>
      <div class="mc-legend-guide-bar"></div>
      <span class="mc-legend-guide-tier mid">50 (Moderate Glow)</span>
      <div class="mc-legend-guide-bar"></div>
      <span class="mc-legend-guide-tier max">100 (Max: Spectacular Peak)</span>
    `;
    container.appendChild(guideEl);

    // Create daily grid container for the two events
    const gridContainer = document.createElement('div');
    gridContainer.className = 'mc-daily-container';
    container.appendChild(gridContainer);

    // Filter active models for chart rendering
    const activeResults = results.filter(r => !disabledModels.has(r.model.id.toLowerCase()));

    for (const ev of events) {
      const card = document.createElement('div');
      card.className = 'mc-daily-card';

      const header = document.createElement('div');
      header.className = 'mc-daily-header';
      header.innerHTML = `
        <span>${ev.icon} ${ev.label}</span>
        <span style="font-size: 0.65rem; color: #94a3b8; font-weight: normal;">Grouped Model Comparison</span>
      `;
      card.appendChild(header);

      const canvasWrap = document.createElement('div');
      canvasWrap.className = 'mc-daily-canvas-wrap';
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position: absolute; left: 0; top: 0; width: 100% !important; height: 100% !important;';
      canvasWrap.appendChild(canvas);
      card.appendChild(canvasWrap);
      gridContainer.appendChild(card);

      const datasets: any[] = [];
      let minScore = Infinity;
      let maxScore = -Infinity;

      for (const r of activeResults) {
        const fc = r.forecast;
        const color = this.getModelColor(r.model.id);
        const dayScores: (number | null)[] = [];

        // Select metric array from DecodedPointForecast based on event key
        let metricArray: number[] | undefined;
        if (ev.key === 'sunrise') {
          metricArray = fc?.sunriseScore;
        } else if (ev.key === 'sunset') {
          metricArray = fc?.sunsetScore;
        } else if (ev.key === 'goldenhour_morning') {
          metricArray = fc?.goldenHourMorning;
        } else if (ev.key === 'goldenhour_evening') {
          metricArray = fc?.goldenHourEvening;
        } else if (ev.key === 'alpenglow_morning') {
          metricArray = fc?.alpenglowMorning;
        } else if (ev.key === 'alpenglow_evening') {
          metricArray = fc?.alpenglowEvening;
        }

        const maxHours = fc ? fc.numHours : 0;
        const cycleEpoch = fc ? fc.cycleEpoch : 0;

        for (const dateStr of dateStrings) {
          if (!fc || !metricArray || metricArray.length === 0 || maxHours === 0) {
            dayScores.push(null);
            continue;
          }

          let scoreForDay: number | null = null;
          // Find step matching this local date in timezone tz
          for (let h = 0; h < maxHours; h++) {
            const stepDate = new Date((cycleEpoch + h * 3600) * 1000);
            const stepDateStr = stepDate.toLocaleDateString('en-CA', { timeZone: tz });
            if (stepDateStr === dateStr) {
              scoreForDay = metricArray[h] ?? 0;
              break;
            }
          }

          dayScores.push(scoreForDay);
          if (scoreForDay !== null && scoreForDay > 0) {
            if (scoreForDay < minScore) minScore = scoreForDay;
            if (scoreForDay > maxScore) maxScore = scoreForDay;
          }
        }

        datasets.push({
          label: r.model.label,
          data: dayScores,
          backgroundColor: color + 'cc',
          hoverBackgroundColor: color,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 3,
          maxBarThickness: 32
        });
      }

      if (minScore === Infinity) { minScore = 0; maxScore = 100; }

      let yMin: number = 0;
      let yMax: number = 100;
      if (scaleMode === 'auto' && maxScore > 0) {
        yMin = Math.max(0, Math.floor(minScore - 5));
        yMax = Math.min(100, Math.ceil(maxScore + 5));
        if (yMax - yMin < 20) {
          yMin = Math.max(0, yMin - 5);
          yMax = Math.min(100, yMax + 5);
        }
      }

      const chart = new Chart(canvas, {
        type: 'bar',
        data: { labels: dayLabels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 250 },
          interaction: { mode: 'index', intersect: false },
          layout: {
            padding: { top: 4, bottom: 2, left: 2, right: 6 }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const val = ctx.parsed.y;
                  if (val === null || val === undefined) return ` ${ctx.dataset.label}: Unavail`;
                  return ` ${ctx.dataset.label}: ${val} / 100 (${ModelComparisonChartService.getScoreQualitativeTier(val, productKey)})`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(255, 255, 255, 0.05)' },
              ticks: {
                color: 'rgba(255, 255, 255, 0.75)',
                font: { size: 9, weight: 'bold' }
              }
            },
            y: {
              min: yMin,
              max: yMax,
              grid: { color: 'rgba(255, 255, 255, 0.08)' },
              title: {
                display: true,
                text: 'Vibrancy Score (0-100)',
                color: 'rgba(255, 255, 255, 0.65)',
                font: { size: 8, weight: 'bold' }
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.7)',
                font: { size: 9 },
                callback: (v: any) => {
                  const num = Number(v);
                  if (num >= 95 || (scaleMode === 'auto' && num >= yMax - 2)) return `${num} (Max - Vivid/Burn)`;
                  if (num >= 60 && num <= 79) return `${num} (Above Avg)`;
                  if (num >= 40 && num <= 59) return `${num} (Avg/Clear)`;
                  if (num >= 21 && num <= 30) return `${num} (Muted)`;
                  if (num <= 5 || (scaleMode === 'auto' && num <= yMin + 2)) return `${num} (Min - Poor/Occluded)`;
                  return `${num}`;
                }
              }
            }
          }
        }
      });

      this.activeCharts.push(chart);
    }

    requestAnimationFrame(() => {
      this.resizeAll();
    });

    return modelMeta;
  }

  private static renderEphemerisRibbon(
    container: HTMLElement,
    timestamps: number[],
    tz: string,
    lat: number,
    lon: number
  ): void {
    container.innerHTML = '';
    const numSteps = timestamps.length;
    if (numSteps < 2) return;

    const ribbonWrap = document.createElement('div');
    ribbonWrap.className = 'model-comparison-sun-moon-track';
    ribbonWrap.style.cssText = 'position: relative; width: 100%; height: 26px; display: flex; align-items: center; box-sizing: border-box; padding: 0 4px;';

    const totalSteps = numSteps - 1;
    const colorStops: string[] = [];
    const overnightSteps: { idx: number; pct: number; sunAlt: number; date: Date }[] = [];

    timestamps.forEach((ts, idx) => {
      const stepDate = new Date(ts);
      const pct = totalSteps > 0 ? (idx / totalSteps) * 100 : 0;

      const sunPos = SunCalc.getPosition(stepDate, lat, lon);
      const sunAltDeg = sunPos.altitude;

      const moonPos = SunCalc.getMoonPosition(stepDate, lat, lon);
      const moonAltDeg = moonPos.altitude;

      const moonIllum = SunCalc.getMoonIllumination(stepDate);

      let color = '#facc15';
      if (sunAltDeg > -0.83) {
        color = '#facc15'; // Day yellow
      } else if (sunAltDeg > -6.0) {
        color = '#f97316'; // Golden hour orange
      } else if (sunAltDeg > -12.0) {
        color = '#2563eb'; // Blue hour
      } else {
        // Night
        if (moonAltDeg <= 0) {
          color = '#030712'; // Black (Moon set)
        } else {
          const greyVal = Math.round(40 + moonIllum.fraction * 150);
          color = `rgb(${greyVal}, ${greyVal}, ${Math.min(255, greyVal + 20)})`;
        }
        overnightSteps.push({ idx, pct, sunAlt: sunAltDeg, date: stepDate });
      }

      colorStops.push(`${color} ${pct.toFixed(2)}%`);
    });

    // Continuous Sun & Moon gradient line
    const lineEl = document.createElement('div');
    lineEl.className = 'timeline-sun-moon-gradient-line';
    lineEl.style.cssText = `position: absolute; left: 0; width: 100%; height: 4px; border-radius: 2px; top: 50%; transform: translateY(-50%); box-shadow: 0 1px 3px rgba(0,0,0,0.5); background: linear-gradient(to right, ${colorStops.join(', ')});`;
    ribbonWrap.appendChild(lineEl);

    // Floating Moon Phase Badges centered over each night
    if (overnightSteps.length > 0) {
      const nightGroups: Record<string, typeof overnightSteps> = {};
      overnightSteps.forEach(s => {
        const nightKey = s.date.toLocaleDateString('en-CA', { timeZone: tz });
        if (!nightGroups[nightKey]) nightGroups[nightKey] = [];
        nightGroups[nightKey].push(s);
      });

      Object.values(nightGroups).forEach(group => {
        let minStep = group[0];
        group.forEach(s => {
          if (s.sunAlt < minStep.sunAlt) minStep = s;
        });

        const moonPhase = getMoonPhaseDetails(minStep.date);
        const pctStr = moonPhase.illumination ? moonPhase.illumination.replace(/\s*Illumination/i, '').trim() : '';

        const badge = document.createElement('div');
        badge.className = 'timeline-moon-phase-badge';
        badge.style.position = 'absolute';
        badge.style.left = `${minStep.pct}%`;
        badge.style.top = '50%';
        badge.style.transform = 'translate(-50%, -50%)';
        badge.style.zIndex = '3';
        badge.style.pointerEvents = 'auto';
        badge.style.cursor = 'default';
        badge.title = `${moonPhase.name} (${pctStr}) - ${minStep.date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz })}`;
        badge.innerHTML = `<span>${moonPhase.icon}</span><span style="font-size: 0.65rem; opacity: 0.95;">${pctStr}</span>`;

        ribbonWrap.appendChild(badge);
      });
    }

    container.appendChild(ribbonWrap);
  }

  public static updateCursor(currentTimeStep: Date | null): void {
    this.currentTimeStepMs = currentTimeStep ? currentTimeStep.getTime() : null;
    if (!currentTimeStep || this.currentTimestamps.length < 2) {
      for (const chart of this.activeCharts) {
        try { chart.update('none'); } catch (_e) { }
      }
      return;
    }
    const targetMs = currentTimeStep.getTime();

    let closestIdx = 0;
    let minDiff = Infinity;
    for (let i = 0; i < this.currentTimestamps.length; i++) {
      const diff = Math.abs(this.currentTimestamps[i] - targetMs);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = i;
      }
    }

    for (const chart of this.activeCharts) {
      try {
        if (chart.data.datasets && chart.data.datasets.length > 0) {
          chart.setActiveElements([{ datasetIndex: 0, index: closestIdx }]);
          chart.tooltip?.setActiveElements([{ datasetIndex: 0, index: closestIdx }], { x: 0, y: 0 });
        }
        chart.update('none');
      } catch (_e) {
        // ignore
      }
    }
  }
}
